import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, BarChart3 } from "lucide-react";
import type { AnalyticsResponse } from "../../shared/types";
import { api } from "../lib/api";
import { number, statusLabels } from "../lib/format";
import { PageHeader } from "../components/app-shell";
import { Empty, ErrorState, Loading, Tag } from "../components/ui";

export function AnalyticsPage() {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const query = useQuery({
    queryKey: ["analytics", days],
    queryFn: () => api<AnalyticsResponse>(`/api/analytics?days=${days}`),
  });
  const change = query.data
    ? query.data.previousPeriodClicks === 0
      ? query.data.totalClicks > 0 ? 100 : 0
      : ((query.data.totalClicks - query.data.previousPeriodClicks) / query.data.previousPeriodClicks) * 100
    : 0;
  return (
    <div className="page analytics-page">
      <PageHeader eyebrow="TRAFFIC · 点击统计" title="访问走势" body="聚合到每日的点击量，不保存访客明细；看趋势，也看哪些短链承担了主要流量。" actions={<div className="segments">{([7, 30, 90] as const).map((value) => <button key={value} type="button" className={days === value ? "segment segment--active" : "segment"} onClick={() => setDays(value)}>{value} 天</button>)}</div>} />
      {query.isLoading ? <Loading label="计算统计" /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : query.data ? (
        <>
          <section className="analytics-hero">
            <div className="analytics-total">
              <span className="eyebrow">TOTAL CLICKS · {days} DAYS</span>
              <strong>{number(query.data.totalClicks)}</strong>
              <p className={change >= 0 ? "trend trend--up" : "trend trend--down"}>{change >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}{Math.abs(change).toFixed(1)}% 较上一周期</p>
            </div>
            <TrafficChart data={query.data} />
          </section>
          <section className="analytics-grid">
            <div className="ranking-panel">
              <header><div><span className="eyebrow">RANK · ALL TIME</span><h2>高频短链</h2></div><BarChart3 size={20} /></header>
              {query.data.topLinks.length ? (
                <div className="ranking-list">
                  {query.data.topLinks.map((link, index) => {
                    const max = query.data?.topLinks[0]?.clickCount || 1;
                    return <div className="ranking-row" key={link.id}><span className="ranking-row__index">{String(index + 1).padStart(2, "0")}</span><div><strong>/{link.slug}</strong><span>{link.targetHost}</span><i style={{ width: `${Math.max(3, (link.clickCount / max) * 100)}%` }} /></div><b>{number(link.clickCount)}</b></div>;
                  })}
                </div>
              ) : <Empty title="还没有点击" body="短链产生访问后，排行会自动出现。" />}
            </div>
            <div className="status-panel">
              <header><span className="eyebrow">INVENTORY · STATUS</span><h2>状态分布</h2></header>
              <div className="status-bars">
                {query.data.statusBreakdown.map((item) => {
                  const total = query.data?.statusBreakdown.reduce((sum, current) => sum + current.count, 0) || 1;
                  return <div key={item.status}><span><Tag tone={item.status === "pending" || item.status === "grace" ? "accent" : "neutral"}>{statusLabels[item.status]}</Tag><b>{number(item.count)}</b></span><i><em style={{ width: `${(item.count / total) * 100}%` }} /></i></div>;
                })}
              </div>
              <p className="panel-footnote">释放后的目标地址会被清除，但状态数量保留在链接目录中，便于治理。</p>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function TrafficChart({ data }: { data: AnalyticsResponse }) {
  const width = 860;
  const height = 260;
  const padX = 18;
  const padY = 24;
  const max = Math.max(1, ...data.daily.map((point) => point.count));
  const step = (width - padX * 2) / Math.max(1, data.daily.length - 1);
  const points = data.daily.map((point, index) => ({
    ...point,
    x: padX + index * step,
    y: height - padY - (point.count / max) * (height - padY * 2),
  }));
  const line = points.map((point) => `${point.x},${point.y}`).join(" ");
  const area = points.length ? `${padX},${height - padY} ${line} ${points.at(-1)?.x ?? width - padX},${height - padY}` : "";
  return (
    <figure className="traffic-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${data.days} 天点击折线图`}>
        {[0, 1, 2, 3, 4].map((row) => <line key={row} x1="0" x2={width} y1={padY + row * ((height - padY * 2) / 4)} y2={padY + row * ((height - padY * 2) / 4)} className="chart-gridline" />)}
        {area ? <polygon points={area} className="chart-area" /> : null}
        {line ? <polyline points={line} className="chart-line" /> : null}
        {points.map((point, index) => (index % Math.max(1, Math.floor(points.length / 8)) === 0 || index === points.length - 1) ? <circle key={point.day} cx={point.x} cy={point.y} r="3" className="chart-dot"><title>{point.day}: {point.count}</title></circle> : null)}
      </svg>
      <figcaption><span>{data.daily[0]?.day ?? "—"}</span><span>UTC · DAILY AGGREGATE</span><span>{data.daily.at(-1)?.day ?? "—"}</span></figcaption>
    </figure>
  );
}

